import { isSubtaskContributor, subtaskTimeEntries } from '../subtaskTime'
import type { Campaign, ReportArea, Task, TaskStatus, UserProfile } from '../types'
import { taskMatchesOperation } from './operationScope'

export const collaboratorFeedbackStorageKey = 'startracker-collaborator-feedback-v1'

export const collaboratorEvaluationOptions = {
  competence: [
    { value: 'competent', label: 'Competente', points: 10 },
    { value: 'committed', label: 'Esforçado', points: 6 },
    { value: 'low', label: 'Pouca competência', points: 2 },
  ],
  productivity: [
    { value: 'productive', label: 'Produtivo', points: 10 },
    { value: 'average', label: 'Mediano', points: 6 },
    { value: 'low', label: 'Pouco produtivo', points: 2 },
  ],
  intelligence: [
    { value: 'intelligent', label: 'Inteligente', points: 10 },
    { value: 'average', label: 'Mediano', points: 6 },
    { value: 'low', label: 'Pouco inteligente', points: 2 },
  ],
} as const

export type CompetenceRating = typeof collaboratorEvaluationOptions.competence[number]['value']
export type ProductivityRating = typeof collaboratorEvaluationOptions.productivity[number]['value']
export type IntelligenceRating = typeof collaboratorEvaluationOptions.intelligence[number]['value']

export interface CollaboratorEvaluation {
  competence: CompetenceRating
  productivity: ProductivityRating
  intelligence: IntelligenceRating
}

export interface CollaboratorFeedback {
  id: string
  contributor: string
  author: string
  authorName: string
  message: string
  evaluation?: CollaboratorEvaluation
  /** Nota legada, mantida para preservar feedbacks registrados antes da avaliação por critérios. */
  score?: number
  createdOn: string
}

export interface CollaboratorPalantirSnapshot {
  campaigns: number
  deliverables: number
  completed: number
  completedVolume: number
  completionRate: number
  seconds: number
  active: number
  executingTasks: number
  alterations: number
  collaborations: number
  onTimeRate?: number
  areas: Array<{ area: ReportArea | 'Não classificada'; volume: number; percentage: number }>
  statuses: Array<{ status: TaskStatus; total: number }>
  recentDeliveries: Array<{ id: string; title: string; campaign: string; completedAt: string; volume: number }>
}

function inRange(value: string | undefined, start: string, end: string) {
  const day = value?.slice(0, 10)
  return Boolean(day && day >= start && day <= end)
}

export function canViewCollaboratorPalantir(profile: UserProfile) {
  return profile === 'Administrador' || profile === 'Gerente'
}

export function readCollaboratorFeedbacks(raw: string | null): CollaboratorFeedback[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.flatMap(item => {
      if (!item || typeof item !== 'object') return []
      const feedback = item as Partial<CollaboratorFeedback>
      if (!feedback.id || !feedback.contributor || !feedback.author || !feedback.authorName || !feedback.message || !feedback.createdOn) return []
      const evaluation = normalizeCollaboratorEvaluation(feedback.evaluation)
      return [{
        id: feedback.id,
        contributor: feedback.contributor,
        author: feedback.author,
        authorName: feedback.authorName,
        message: feedback.message,
        evaluation,
        score: typeof feedback.score === 'number' && feedback.score >= 0 && feedback.score <= 10 ? feedback.score : undefined,
        createdOn: feedback.createdOn,
      }]
    })
  } catch {
    return []
  }
}

function ratingExists<Key extends keyof typeof collaboratorEvaluationOptions>(key: Key, value: unknown): value is typeof collaboratorEvaluationOptions[Key][number]['value'] {
  return typeof value === 'string' && collaboratorEvaluationOptions[key].some(option => option.value === value)
}

function normalizeCollaboratorEvaluation(value: unknown): CollaboratorEvaluation | undefined {
  if (!value || typeof value !== 'object') return undefined
  const evaluation = value as Partial<CollaboratorEvaluation>
  if (!ratingExists('competence', evaluation.competence)
    || !ratingExists('productivity', evaluation.productivity)
    || !ratingExists('intelligence', evaluation.intelligence)) return undefined
  return {
    competence: evaluation.competence,
    productivity: evaluation.productivity,
    intelligence: evaluation.intelligence,
  }
}

export function collaboratorEvaluationScore(evaluation: CollaboratorEvaluation) {
  const competence = collaboratorEvaluationOptions.competence.find(option => option.value === evaluation.competence)?.points ?? 0
  const productivity = collaboratorEvaluationOptions.productivity.find(option => option.value === evaluation.productivity)?.points ?? 0
  const intelligence = collaboratorEvaluationOptions.intelligence.find(option => option.value === evaluation.intelligence)?.points ?? 0
  return Math.round((competence + productivity + intelligence) / 3 * 10) / 10
}

export function collaboratorEvaluationLabel<Key extends keyof typeof collaboratorEvaluationOptions>(key: Key, value: CollaboratorEvaluation[Key]) {
  return collaboratorEvaluationOptions[key].find(option => option.value === value)?.label ?? value
}

export function collaboratorStarScore(feedbacks: CollaboratorFeedback[], contributor: string) {
  const scores = feedbacks
    .filter(item => item.contributor === contributor)
    .flatMap(item => item.evaluation ? [collaboratorEvaluationScore(item.evaluation)] : typeof item.score === 'number' ? [item.score] : [])
  if (!scores.length) return undefined
  return Math.round(scores.reduce((total, score) => total + score, 0) / scores.length * 10) / 10
}

export function buildCollaboratorPalantir(tasks: Task[], campaigns: Campaign[], operation: string, start: string, end: string, contributor: string): CollaboratorPalantirSnapshot {
  const scopedTasks = tasks.filter(task => taskMatchesOperation(task, campaigns, operation))
  const campaignIds = new Set<string>()
  const areaVolumes = new Map<ReportArea | 'Não classificada', number>()
  const statusTotals = new Map<TaskStatus, number>()
  const recentDeliveries: CollaboratorPalantirSnapshot['recentDeliveries'] = []
  let deliverables = 0
  let completed = 0
  let completedVolume = 0
  let seconds = 0
  let active = 0
  const executingTaskIds = new Set<string>()
  let alterations = 0
  let collaborations = 0
  let datedCompletions = 0
  let onTimeCompletions = 0

  scopedTasks.forEach(task => {
    const campaign = campaigns.find(item => item.id === task.campaignId)
    if (task.owner === contributor && !['Aprovada', 'Reprovada'].includes(task.status)) executingTaskIds.add(`task:${task.id}`)
    task.subtasks.forEach(subtask => {
      if (!isSubtaskContributor(subtask, contributor)) return
      const periodEntries = subtaskTimeEntries(subtask).filter(entry => entry.user === contributor && inRange(entry.startedOn ?? subtask.completedAt, start, end))
      const completedInPeriod = subtask.status === 'Aprovada' && inRange(subtask.completedAt, start, end)
      const activeForContributor = subtask.owner === contributor && !['Aprovada', 'Reprovada'].includes(subtask.status)
      const hasPeriodWork = periodEntries.length > 0 || completedInPeriod

      if (hasPeriodWork) {
        campaignIds.add(task.campaignId)
        deliverables += 1
      }
      seconds += periodEntries.reduce((total, entry) => total + entry.seconds, 0)
      if (activeForContributor) active += 1
      if (activeForContributor) executingTaskIds.add(`subtask:${subtask.id}`)
      if ((subtask.supportUsers ?? []).includes(contributor) && hasPeriodWork) collaborations += 1
      if (hasPeriodWork || activeForContributor) statusTotals.set(subtask.status, (statusTotals.get(subtask.status) ?? 0) + 1)

      if (!completedInPeriod) return
      completed += 1
      completedVolume += subtask.volume
      const area = subtask.reportArea ?? task.reportArea ?? campaign?.reportArea ?? 'Não classificada'
      areaVolumes.set(area, (areaVolumes.get(area) ?? 0) + subtask.volume)
      recentDeliveries.push({ id: subtask.id, title: subtask.title, campaign: campaign?.name ?? task.title, completedAt: subtask.completedAt!, volume: subtask.volume })
      if (subtask.dueAt) {
        datedCompletions += 1
        if (subtask.completedAt! <= subtask.dueAt.slice(0, 10)) onTimeCompletions += 1
      }
    })

    alterations += task.activityEntries.filter(entry => {
      if (entry.statusChange?.to !== 'Em alteração' || !inRange(entry.occurredOn, start, end)) return false
      if (entry.statusChange.entity === 'task') return task.owner === contributor
      return task.subtasks.some(subtask => subtask.id === entry.statusChange?.entityId && isSubtaskContributor(subtask, contributor))
    }).length
  })

  const areaTotal = [...areaVolumes.values()].reduce((total, volume) => total + volume, 0)
  return {
    campaigns: campaignIds.size,
    deliverables,
    completed,
    completedVolume,
    completionRate: deliverables ? Math.round(completed / deliverables * 100) : 0,
    seconds,
    active,
    executingTasks: executingTaskIds.size,
    alterations,
    collaborations,
    onTimeRate: datedCompletions ? Math.round(onTimeCompletions / datedCompletions * 100) : undefined,
    areas: [...areaVolumes.entries()]
      .map(([area, volume]) => ({ area, volume, percentage: areaTotal ? Math.round(volume / areaTotal * 100) : 0 }))
      .sort((left, right) => right.volume - left.volume),
    statuses: [...statusTotals.entries()].map(([status, total]) => ({ status, total })).sort((left, right) => right.total - left.total),
    recentDeliveries: recentDeliveries.sort((left, right) => right.completedAt.localeCompare(left.completedAt)).slice(0, 6),
  }
}
